// src/components/MobilePhoneAnimation.js
import React, { useRef, useEffect } from 'react';
import * as THREE from 'three';

const MobilePhoneAnimation = () => {
  const mountRef = useRef(null);

  useEffect(() => {
    const container = mountRef.current;
    if (!container) return;

    let renderer, camera, scene, phoneMesh; // Keep track of objects
    let animationFrameId; // Store animation frame request id

    const init = () => {
        const width = container.clientWidth;
        const height = container.clientHeight;

        if (width === 0 || height === 0) return; // Don't init if no size

        scene = new THREE.Scene();
        // Set background transparent to inherit navbar color
        // scene.background = new THREE.Color(0x000000);

        // Adjust camera for potentially smaller aspect ratio
        camera = new THREE.PerspectiveCamera(50, width / height, 0.1, 1000); // Reduced FOV slightly
        camera.position.z = 3.5; // Slightly further back

        renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
        renderer.setSize(width, height);
        renderer.setPixelRatio(window.devicePixelRatio);

        while (container.firstChild) { // Clear previous canvas if any
            container.removeChild(container.firstChild);
        }
        container.appendChild(renderer.domElement);

        // Phone shape (kept original logic)
        const phoneShape = new THREE.Shape();
        const phoneWidth = 1; const phoneHeight = 2; const cornerRadius = 0.15;
        phoneShape.moveTo(-phoneWidth/2 + cornerRadius, -phoneHeight/2);
        phoneShape.lineTo(phoneWidth/2 - cornerRadius, -phoneHeight/2);
        phoneShape.quadraticCurveTo(phoneWidth/2, -phoneHeight/2, phoneWidth/2, -phoneHeight/2 + cornerRadius);
        phoneShape.lineTo(phoneWidth/2, phoneHeight/2 - cornerRadius);
        phoneShape.quadraticCurveTo(phoneWidth/2, phoneHeight/2, phoneWidth/2 - cornerRadius, phoneHeight/2);
        phoneShape.lineTo(-phoneWidth/2 + cornerRadius, phoneHeight/2);
        phoneShape.quadraticCurveTo(-phoneWidth/2, phoneHeight/2, -phoneWidth/2, phoneHeight/2 - cornerRadius);
        phoneShape.lineTo(-phoneWidth/2, -phoneHeight/2 + cornerRadius);
        phoneShape.quadraticCurveTo(-phoneWidth/2, -phoneHeight/2, -phoneWidth/2 + cornerRadius, -phoneHeight/2);

        const extrudeSettings = { depth: 0.1, bevelEnabled: false };
        const geometry = new THREE.ExtrudeGeometry(phoneShape, extrudeSettings);
        geometry.center();

        // White wireframe to show on black navbar background
        const material = new THREE.MeshBasicMaterial({ color: 0xffffff, wireframe: true });
        phoneMesh = new THREE.Mesh(geometry, material);
        // Adjust scale if needed for smaller container
        // phoneMesh.scale.set(0.8, 0.8, 0.8);
        scene.add(phoneMesh);

        animate();
    };

    const animate = () => {
      animationFrameId = requestAnimationFrame(animate);
      if (phoneMesh && renderer && scene && camera) { // Check objects exist
          phoneMesh.rotation.y += 0.01;
          phoneMesh.rotation.x += 0.005;
          renderer.render(scene, camera);
      }
    };

    // Use ResizeObserver for better resize handling
    const resizeObserver = new ResizeObserver(entries => {
        if (!entries || entries.length === 0) return;
        if (!renderer || !camera || !container) return;

        const { width, height } = entries[0].contentRect;

        if (width > 0 && height > 0) {
            renderer.setSize(width, height);
            camera.aspect = width / height;
            camera.updateProjectionMatrix();
        }
    });

    if (container) {
        resizeObserver.observe(container);
        init(); // Initialize after observing
    }

    return () => {
      resizeObserver.disconnect();
      cancelAnimationFrame(animationFrameId);
      if (renderer) {
          if (container && container.contains(renderer.domElement)) {
              container.removeChild(renderer.domElement);
          }
          renderer.dispose();
      }
      phoneMesh?.geometry?.dispose();
      phoneMesh?.material?.dispose();
       scene = null; camera = null; renderer = null; phoneMesh = null; // Help GC
    };
  }, []); // Empty dependency array

  return (
    <div
      ref={mountRef}
      style={{
        width: '100%',
        height: '100%', // Changed from fixed 150px to 100%
        display: 'block', // Keep as block
        overflow: 'hidden', // Hide parts of mesh if they clip edges
      }}
    />
  );
};

export default MobilePhoneAnimation;